import { h } from 'preact'
import { useMemo } from 'preact/hooks'
import classNames from 'clsx'

import BackgroundTitle from '../../atom/BackgroundTitle'
import Codeblock from '../../atom/Codeblock'
import Figure from '../../atom/nbb-figure'
import SmartImg, { SmartImgSettingsProvider } from '../../atom/nbb-smart-img'
import Table from '../../atom/Table'
import Gallery from '../../organism/nbb-gallery'
import Aside from '../Aside'
import { getDisplayName } from '../../utils'
import { buildFloatingClassName } from '../../util/Floating'
import { buildBoxClassNames } from '../../util/Box'
import { SIDE_LEFT } from '../../../constants'

import { buildBreakClassNames, buildOffsetClassNames } from './classNames'
import { IMAGE_SETTINGS } from './img'
import styles from './index.module.scss'

export const AsArticleContentEntry = (Component, displayName) => {
  const WrappedComponent = (props) => {
    const {
      float,
      boxed,
      offset,
      break: breakContent,
      padded,
      className,
      ...rest
    } = props

    const classes = useMemo(
      () => buildClassNames({
        className,
        float,
        boxed,
        offset,
        padded,
        breakContent,
      }),
      [
        className,
        float,
        boxed,
        offset,
        padded,
        breakContent,
      ],
    )

    const side = float || breakContent || offset
    const hasImageSettings = side in IMAGE_SETTINGS

    const children = (
      <Component
        {...rest}
        className={classes}
      />
    )

    return !hasImageSettings ? children : (
      <SmartImgSettingsProvider {...IMAGE_SETTINGS[side]}>
        {children}
      </SmartImgSettingsProvider>
    )
  }

  WrappedComponent.displayName = displayName || `AsArticleContentEntry(${getDisplayName(Component)})`

  return WrappedComponent
}

function buildClassNames(props) {
  const {
    className,
    boxed,
    float,
    offset,
    padded,
    breakContent,
  } = props

  const boxedOpts = boxed && (
    boxed === true ? {} : boxed
  )

  return classNames(
    className,
    styles['m-article-content__entry'],
    { [styles['m-article-content__entry--padded']]: padded },
    boxedOpts && buildBoxClassNames(boxedOpts),
    float && buildFloatingClassName({
      float,
      side: float,
    }),
    offset && buildOffsetClassNames({
      offset,
      side: offset,
    }),
    breakContent && buildBreakClassNames({ side: breakContent }),
  )
}

const ArticleContentEntry = AsArticleContentEntry('div', 'ArticleContentEntry')

export const ArticleAside = AsArticleContentEntry(Aside, 'ArticleAside')
export const ArticleCodeblock = AsArticleContentEntry(Codeblock, 'ArticleCodeblock')
export const ArticleGallery = AsArticleContentEntry(Gallery, 'ArticleGallery')
export const ArticleImg = AsArticleContentEntry(SmartImg, 'ArticleImg')
export const ArticleTable = AsArticleContentEntry(Table, 'ArticleTable')
export const ArticleTitle = AsArticleContentEntry(BackgroundTitle, 'ArticleTitle')

const ArticleContentFigure = AsArticleContentEntry(Figure)

export const ArticleFigure = (props) => (
  <ArticleContentFigure
    {...props}
    className={buildFigureClassNames(props)}
  />
)

function buildFigureClassNames({ className, offset }) {
  return classNames(
    className,
    {
      [styles['m-article-content__figure--wide']]: offset === true,
    },
  )
}

export const ArticleHeading1 = (props) => (
  <ArticleTitle level={1} break={SIDE_LEFT}  {...props} />
)

export const ArticleHeading2 = (props) => (
  <ArticleTitle level={2} break={SIDE_LEFT} small {...props} />
)

export const ArticleHeading3 = (props) => (
  <ArticleTitle level={3} corner={false} noPadding small light {...props} />
)

export const ArticleHeading4 = (props) => (
  <ArticleTitle level={4} corner={false} noPadding small light {...props} />
)

export const ArticleHeading5 = (props) => (
  <ArticleTitle level={5} corner={false} noPadding small light {...props} />
)

export const ArticleHeading6 = (props) => (
  <ArticleTitle level={6} corner={false} noPadding small light {...props} />
)

export default ArticleContentEntry
